import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from '../routing';
import { analysisApi, suppliersApi, formatCurrency } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { currentMonthRange, periodLabel, PRESETS_ANALYTIC, readPeriodParams, withPeriodParams } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import { useSortable } from '../utils/useSortable';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import { topProductKey, rowChangePct } from '../utils/topProducts';
import { track, EVENTS } from '../utils/analytics';
import Icon from '../components/Icon';
import EmptyState from '../components/EmptyState';
import ExportMenu from '../components/ExportMenu';
import FilterButton from '../components/FilterButton';
import FilterSheet from '../components/FilterSheet';
import InlineLoadError from '../components/InlineLoadError';
import MultiSelectFilter from '../components/MultiSelectFilter';
import PeriodPicker from '../components/PeriodPicker';
import TopProductSheet, { RowChangeChip } from '../components/TopProductSheet';
import { TopProductOrdersPanel } from '../components/TopProductOrders';
import ExpandCaret from '../components/ExpandCaret';
import PriceRange from '../components/PriceRange';
import { TopProductRow, TopProductsColumnHead } from '../components/TopProductReportRows';
import PageViewAsMenu from '../components/ViewAsMenu';

// המוצרים המובילים ברכש — the full ranking behind the /analysis card: every
// product bought in the period, most money first, with what it cost (min / max /
// current, % change) and, one tap away, the orders behind it. Its Excel holds
// exactly these rows (same params, same server function — lib/topProducts.js).
//
// Reached from the card's title, ⋮ and לצפייה, which forward the period (and the
// supplier filter when one is on). Behind the restaurant's top_products_enabled
// flag; every role may read it.
//
// Filters: the period, suppliers and a product search — from the start of a
// word in the name, or the start of the SKU (lib/topProducts.js
// matchesProductSearch). ALL of them go to the server, which applies the search after
// ranking (a found product keeps its rank) — so the screen and the Excel hold
// the same rows by construction, never two implementations of one filter.
//
// Each row leads with the LAST PRICE and its change; the period total, which
// the ranking is by, is the next column (owner, 20/10/2026).

// The ranking can run to a few thousand rows for a large restaurant over a
// year; render it a page at a time rather than all at once.
const PAGE = 100;
const money = (n) => (n == null ? '—' : formatCurrency(n));

export default function TopProductsReportPage() {
  const { topProductsEnabled, settingsLoaded } = useSettings();
  // Gate before anything mounts that would fire a request the server answers
  // with 403 (same reasoning as RequireDishModule in App.js). Not loaded yet →
  // an honest blank instant, never a false "not available".
  if (!settingsLoaded) return null;
  if (!topProductsEnabled) {
    return (
      <div style={{ padding: '64px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.15rem', margin: '0 0 8px' }}>הדוח אינו פעיל למסעדה זו</h1>
        <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', margin: '0 0 16px' }}>
          לשאלות על הפעלת הדוח פנו לצוות Peak.
        </p>
        <Link className="btn btn-primary btn-sm" to="/analysis">חזרה לניתוח רכש</Link>
      </div>
    );
  }
  return <TopProductsReport />;
}

function TopProductsReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  // List rows below 1200px, the table above it. Even trimmed, the table needs
  // ~1100px; at tablet widths it scrolled sideways and wrapped product names
  // onto three lines (owner screenshot, 20/10/2026). The list rows are the
  // card's own, so a tablet reads the same row as the card it came from.
  const useList = useIsMobile(1199);
  const phone = useIsMobile(640);

  const initial = currentMonthRange();
  const urlRange = readPeriodParams(searchParams, PRESETS_ANALYTIC);
  const [dateFrom, setDateFrom] = useState(urlRange.from || initial.from);
  const [dateTo, setDateTo] = useState(urlRange.to || initial.to);
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, (f, t) => { setDateFrom(f); setDateTo(t); });
  const [supplierIds, setSupplierIdsRaw] = useState(
    () => (searchParams.get('supplier_ids') || '').split(',').filter(Boolean),
  );
  // "No supplier" is not "no filter" (Codex review, PR #309): /analysis sends
  // a PRESENT-but-empty supplier_ids when its name/product filter matched no
  // supplier, and the card then shows nothing. Dropping it here opened every
  // product, and the Excel exported every supplier. So an explicitly empty
  // list arriving in the URL is kept as its own state, carried through the
  // requests and the URL, until the user picks suppliers or clears the filter.
  const [noSupplierMatch, setNoSupplierMatch] = useState(
    () => searchParams.has('supplier_ids') && (searchParams.get('supplier_ids') || '').split(',').filter(Boolean).length === 0,
  );
  const setSupplierIds = (ids) => { setNoSupplierMatch(false); setSupplierIdsRaw(ids); };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  // A product's orders: a bottom sheet on a phone, sub-lines under its row
  // from 641px — one open at a time, like רכש לפי ספקים (owner, 22/10/2026).
  const [sheetRow, setSheetRow] = useState(null);
  // { key, scope }: the open row and the scope (period, suppliers, search) it
  // was opened in — see openScope below.
  const [openRow, setOpenRow] = useState(null);
  const [shown, setShown] = useState(PAGE);
  // The typed term, and the one actually sent: debounced, because each search
  // re-ranks on the server and the server is synchronous.
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const filterRef = useRef(null);
  useStickyFilterOffset(filterRef);

  // The server reads a present-but-empty supplier_ids as "no supplier"
  // (nothing at all) and an absent one as every supplier. Unticking the last
  // supplier means "every supplier" — omitted; only the carried-over
  // no-match state sends the empty list.
  const supplierParam = supplierIds.length ? supplierIds.join(',') : (noSupplierMatch ? '' : undefined);
  const scopeParams = useMemo(() => ({
    date_from: dateFrom,
    date_to: dateTo,
    ...(supplierParam !== undefined ? { supplier_ids: supplierParam } : {}),
  }), [dateFrom, dateTo, supplierParam]);
  // The rows (and the Excel) also take the search; a product's orders do not —
  // they are the orders of that row, whatever found it.
  const params = useMemo(() => ({
    ...scopeParams,
    ...(q ? { q } : {}),
  }), [scopeParams, q]);

  // New rows, new question: an open product closes when the period, the
  // suppliers or the search change (Codex, PR #312 and #320). Checked AT
  // RENDER, so the render carrying the new scope never mounts the old panel
  // with it (an effect reset ran only after that render — one wasted orders
  // request); and the stale entry is then DISCARDED, so returning to the old
  // scope (clearing a search) cannot bring the panel back by itself.
  const openScope = JSON.stringify(params);
  const isOpen = (k) => !phone && openRow?.key === k && openRow.scope === openScope;
  const openProduct = (r) => {
    if (phone) { setSheetRow(r); return; }
    const k = topProductKey(r);
    setOpenRow(isOpen(k) ? null : { key: k, scope: openScope });
  };
  useEffect(() => {
    setOpenRow(cur => (cur && cur.scope !== openScope ? null : cur));
  }, [openScope]);

  // replace: Back returns to /analysis instead of stepping through every period tried.
  useEffect(() => {
    const next = withPeriodParams(new URLSearchParams(), presetKey, dateFrom, dateTo);
    if (supplierParam !== undefined) next.set('supplier_ids', supplierParam);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, presetKey, supplierParam]);

  // The full supplier list, not one derived from the loaded rows: a filter that
  // cannot name a supplier you buy from reads as "you have no such supplier".
  useEffect(() => {
    suppliersApi.getAll({ include_empty: true })
      .then(r => setAllSuppliers(r.data || []))
      .catch(() => setAllSuppliers([]));
  }, []);

  const key = JSON.stringify(params);
  const scopeKey = JSON.stringify(scopeParams);
  // A new period or supplier set shows the spinner; a new search keeps the
  // current rows on screen until the answer lands, rather than flashing.
  const loadedScopeRef = useRef(null);
  // A generation counter, not a per-call `alive` flag: the retry button calls
  // fetchData directly, and its late answer must not overwrite a newer search
  // or period (self-review, 20/10/2026).
  const reqIdRef = useRef(0);
  // The request key whose answer is ON SCREEN. A search keeps the previous
  // rows visible until its answer lands, so between the two the requested
  // params and the shown rows differ — and the Excel must follow the screen,
  // not the pending request (Codex review, PR #309).
  const [shownKey, setShownKey] = useState(null);
  const fetchData = useCallback(() => {
    const reqId = ++reqIdRef.current;
    const alive = () => reqId === reqIdRef.current;
    if (loadedScopeRef.current !== scopeKey) setLoading(true);
    setLoadError(false);
    setShown(PAGE);
    analysisApi.getTopProducts({ ...params, limit: 'all' })
      .then(r => {
        if (!alive()) return;
        setRows(Array.isArray(r.data) ? r.data : []);
        setShownKey(key);
        loadedScopeRef.current = scopeKey;
      })
      .catch(() => { if (alive()) { setLoadError(true); setRows([]); setShownKey(null); loadedScopeRef.current = null; } })
      .finally(() => { if (alive()) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const { sorted, indicator, headerProps } = useSortable(rows, {
    rank: r => r.rank,
    name: r => r.name,
    supplier: r => r.supplier_name,
    spend: r => r.spend,
    min: r => r.min_price,
    max: r => r.max_price,
    current: r => r.current_price,
    change: r => { const pct = rowChangePct(r); return pct == null ? null : parseFloat(pct); },
    orders: r => r.doc_count,
  });
  const sortTh = (field, label) => <th {...headerProps(field)}>{label} {indicator(field)}</th>;
  const visible = sorted.slice(0, shown);

  const period = periodLabel(dateFrom, dateTo);
  const supplierIdToName = useMemo(() => new Map(allSuppliers.map(s => [String(s.id), s.name])), [allSuppliers]);
  const supplierOptions = useMemo(() => allSuppliers.map(s => String(s.id)), [allSuppliers]);
  const renderSupplierOption = useCallback((id) => supplierIdToName.get(id) || id, [supplierIdToName]);

  const supplierSelect = (inline) => (
    <MultiSelectFilter
      inline={inline}
      label="ספק"
      values={supplierIds}
      options={supplierOptions}
      onChange={setSupplierIds}
      renderOption={renderSupplierOption}
      searchable
      minSearchChars={2}
    />
  );
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom} dateTo={dateTo}
      onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
      presets={PRESETS_ANALYTIC}
      variant={variant}
    />
  );
  const searchPill = (
    <label className={`docs-filter-control docs-filter-searchpill${qInput ? ' is-active' : ''}`}>
      <Icon name="search" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={qInput}
        onChange={(e) => setQInput(e.target.value)}
        placeholder="שם מוצר או מק״ט…"
        aria-label="חיפוש מוצר"
        style={{ minWidth: isMobile ? 0 : 180 }}
      />
    </label>
  );
  const clearAll = () => { setSupplierIds([]); setQInput(''); setQ(''); };
  const hasChips = supplierIds.length > 0 || noSupplierMatch || Boolean(q);
  const chips = hasChips && (
    <div className="docs-filter-chips">
      {noSupplierMatch && (
        <span className="docs-filter-chip">
          ספקים: אף ספק לא תאם לסינון בניתוח רכש
          <button type="button" className="docs-filter-chip-x" aria-label="נקה סינון ספקים" onClick={() => setSupplierIds([])}>
            <Icon name="close" size={11} strokeWidth={2} />
          </button>
        </span>
      )}
      {supplierIds.length > 0 && (
        <span className="docs-filter-chip">
          ספקים: {supplierIds.map(id => supplierIdToName.get(id) || id).join(', ')}
          <button type="button" className="docs-filter-chip-x" aria-label="נקה סינון ספקים" onClick={() => setSupplierIds([])}>
            <Icon name="close" size={11} strokeWidth={2} />
          </button>
        </span>
      )}
      {q && (
        <span className="docs-filter-chip">
          מוצר: {q}
          <button type="button" className="docs-filter-chip-x" aria-label="נקה חיפוש מוצר" onClick={() => { setQInput(''); setQ(''); }}>
            <Icon name="close" size={11} strokeWidth={2} />
          </button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={clearAll}>נקה הכל</button>
    </div>
  );
  // ≤720px: the period gets its own sticky row and the supplier picker moves
  // into a FilterSheet — the same shape as the by-classification report.
  const filterBar = isMobile ? (
    <>
      <div className="period-bar" ref={filterRef}>{dateRange('bar')}</div>
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {searchPill}
          <FilterButton
            activeCount={supplierIds.length > 0 || noSupplierMatch ? 1 : 0}
            expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          />
        </div>
        {chips}
        <FilterSheet open={sheetOpen} onClose={() => setSheetOpen(false)} resultCount={rows.length} onClearAll={clearAll}>
          {supplierSelect(true)}
        </FilterSheet>
      </div>
    </>
  ) : (
    <div className="docs-filter-wrap" ref={filterRef}>
      <div className="docs-filter-bar">
        {searchPill}
        {supplierSelect(false)}
        {dateRange('pill')}
      </div>
      {chips}
    </div>
  );

  const list = useList ? (
    <div className="top-products">
      <TopProductsColumnHead />
      <div className="m-list">
        {visible.map(r => {
          const k = topProductKey(r);
          const open = isOpen(k);
          return (
            <Fragment key={k}>
              <TopProductRow row={r} onOpen={openProduct} phone={phone} expanded={open} />
              {open && <TopProductOrdersPanel product={r} params={scopeParams} />}
            </Fragment>
          );
        })}
      </div>
    </div>
  ) : (
    // Eight columns: quantity opens with the row's orders (first price is in
    // the Excel), and min–max is the same bar as the list rows.
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            {sortTh('rank', '#')}
            {sortTh('name', 'מוצר')}
            {sortTh('supplier', 'ספק')}
            {sortTh('current', 'מחיר נוכחי')}
            {sortTh('change', '% שינוי')}
            {sortTh('min', 'מינ׳ – מקס׳')}
            {sortTh('spend', 'סה״כ (ללא מע״מ)')}
            {sortTh('orders', 'הזמנות')}
          </tr>
        </thead>
        <tbody>
          {visible.map(r => {
            const k = topProductKey(r);
            const open = isOpen(k);
            return (
              <Fragment key={k}>
                <tr
                  className={open ? 'row-clickable is-open' : 'row-clickable'}
                  onClick={() => openProduct(r)}
                  style={{ background: open ? 'var(--selected-soft-bg)' : undefined }}
                >
                  <td style={{ color: 'var(--ink-subtle)', fontVariantNumeric: 'tabular-nums' }}>{r.rank}</td>
                  <td style={{ fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <ExpandCaret open={open} />{r.name}
                    </span>
                    {r.sku && <div style={{ color: 'var(--ink-subtle)', fontSize: 'var(--fs-xs)', fontWeight: 400 }}>{r.sku}</div>}
                  </td>
                  <td>{r.supplier_name}</td>
                  <td style={{ fontWeight: 700, color: 'var(--figure)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(r.current_price)}</td>
                  <td><RowChangeChip row={r} /></td>
                  <td><PriceRange row={r} /></td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatCurrency(r.spend)}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.doc_count}</td>
                </tr>
                {open && (
                  <tr className="tp-orders-row">
                    <td colSpan={8}><TopProductOrdersPanel product={r} params={scopeParams} /></td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  let body;
  if (loading) body = <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>;
  else if (loadError) body = <InlineLoadError message="לא ניתן לטעון את המוצרים המובילים" onRetry={fetchData} />;
  else if (rows.length === 0) {
    body = (
      <EmptyState compact message={hasChips ? `לא נמצאו מוצרים ב${period} לפי הסינון שנבחר` : `אין רכישות של מוצרים ב${period}`}>
        <p className="empty-subline">אפשר לבחור תקופה אחרת.</p>
      </EmptyState>
    );
  } else {
    body = (
      <>
        <p style={{ margin: '0 0 12px', fontSize: 'var(--fs-sm)', color: 'var(--ink-subtle)' }}>
          {rows.length.toLocaleString('en-US')} מוצרים · לפי סה״כ בתקופה · ללא מע״מ · השינוי לעומת הקנייה הראשונה בתקופה
        </p>
        {list}
        {shown < sorted.length && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShown(n => n + PAGE)}>
              הצג עוד ({(sorted.length - shown).toLocaleString('en-US')})
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="page">
      <div className="breadcrumb">
        <Link className="link" to="/analysis">ניתוח רכש</Link>
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        <span>המוצרים המובילים</span>
      </div>

      <div className="page-title-row">
        <div className="page-title-group">
          {/* "המוצרים המובילים", no "ברכש" (owner, 23/10/2026): the
              breadcrumb already says ניתוח רכש, and the longer title wrapped
              onto two lines on a phone. No "10" — the report lists every
              product. The Excel reports keep the full name. */}
          <h1 className="page-title">המוצרים המובילים</h1>
        </div>
        <div className="page-title-actions">
          <ExportMenu
            scopeLabel={period}
            options={[{
              key: 'top_products',
              label: 'המוצרים המובילים ברכש',
              hint: 'כל המוצרים בתקופה לפי סכום, עם מחיר מינ׳, מקס׳ ונוכחי ו-% שינוי',
              run: () => {
                track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'top_products' });
                // Only when the rows on screen are the answer to these exact
                // params — otherwise the file and the screen would disagree.
                // ExportMenu shows the thrown message inline.
                if (shownKey !== key) throw new Error('הנתונים מתעדכנים — נסו שוב בעוד רגע');
                return analysisApi.exportTopProducts(params);
              },
            }, {
              // Its own report, never folded into the one above (owner,
              // 21/10/2026): the orders are a bigger, different file.
              key: 'top_products_orders',
              label: 'המוצרים המובילים ברכש - כולל הזמנות',
              hint: 'אותו דוח, עם גיליון נוסף: כל הזמנה של כל מוצר ברשימה',
              run: () => {
                track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'top_products_orders' });
                if (shownKey !== key) throw new Error('הנתונים מתעדכנים — נסו שוב בעוד רגע');
                return analysisApi.exportTopProductsWithOrders(params);
              },
            }]}
          />
          <PageViewAsMenu />
        </div>
      </div>

      {filterBar}

      <div className="card">{body}</div>

      {sheetRow && <TopProductSheet product={sheetRow} params={scopeParams} onClose={() => setSheetRow(null)} />}
    </div>
  );
}
